import type { ViewContainer } from 'pixi.js';
import type { AppState } from './AppState';
import { INITIAL_STATE } from './AppState';

type Change = Partial<AppState>;
type Callback = (change: Change, prevVal: Change) => void;
type StateField = keyof AppState;

interface Subscription {
  callback: Callback;
  context: object | undefined;
}

export class AppStateSubscriptions {
  private readonly subscriptions = new Map<string, Subscription[]>();

  public subscribe(callback: Callback, context: object | undefined = undefined): void {
    const callBackParams = callback
      .toString()
      .split(')')[0]
      .match(/[^{}]+(?=\})/g)
      ?.map((field) => field.replace(/\s/g, ''));

    let subscribeFields: string[] = [];

    if (callBackParams) {
      subscribeFields = callBackParams[0].split(',');
      subscribeFields = subscribeFields.map((field) => field.split(':')[0]);
    }

    if (subscribeFields.length > 0) {
      this.onChange(subscribeFields as StateField[], callback);
    } else {
      this.addSubscription('any', callback, context);
    }
  }

  private addSubscription(
    subscribeField: StateField | 'any',
    callback: Callback,
    context: object | undefined = undefined,
  ): void {
    const { subscriptions } = this;
    const subscription: Subscription = { callback, context };

    if (subscriptions.has(subscribeField)) {
      subscriptions.get(subscribeField)?.push(subscription);
    } else {
      subscriptions.set(subscribeField, [subscription]);
    }
  }

  public removeFieldSubscriptions(context: object, subscribeFields: StateField[] | StateField): void {
    const { subscriptions } = this;
    const fields = !Array.isArray(subscribeFields) ? [subscribeFields] : subscribeFields;
    fields.forEach((subscribeField) => {
      const fieldSubscriptions = subscriptions.get(subscribeField);
      if (!fieldSubscriptions) return;

      for (let i = fieldSubscriptions.length - 1; i >= 0; i -= 1) {
        const subscription = fieldSubscriptions[i];
        if (subscription.context === context) {
          fieldSubscriptions.splice(i, 1);
        }
      }
    });
  }

  public removeAllSubscriptions(context: object): void {
    this.removeFieldSubscriptions(context, Object.keys(INITIAL_STATE) as (keyof AppState)[]);
  }

  public contextHasSubscriptions(context: object): boolean {
    const { subscriptions } = this;
    return Array.from(subscriptions.keys()).some((key) => {
      const keySubscriptions = subscriptions.get(key);
      if (!keySubscriptions) return false;
      return keySubscriptions.some((subscription) => subscription.context === context);
    });
  }

  public analyzeChanges(changes: Partial<AppState>, prevValues: Partial<AppState>): void {
    if (this.subscriptions.has('any')) {
      this.subscriptions.get('any')?.forEach(({ callback }) => {
        callback(changes, prevValues);
      });
    }

    this.subscriptions.forEach((callbacks, field) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((changes as Record<string, any>)[field] !== undefined) {
        callbacks.forEach(({ callback }) => {
          callback(changes, prevValues);
        });
      }
    });
  }

  public onChange(
    subscribeFields: StateField[] | StateField,
    callback: Callback,
    context: ViewContainer | object | undefined = undefined,
  ): void {
    const fields = !Array.isArray(subscribeFields) ? [subscribeFields] : subscribeFields;
    fields.forEach((subscribeField) => {
      this.addSubscription(subscribeField, callback, context);
    });
  }
}
